import { Injectable } from '@angular/core';
import { jwtDecode } from 'jwt-decode';
import { AuthEventsService, AuthEventType } from '../auth/auth-events.service';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root'
})
export class TokenService {
  public readonly JWT_STORAGE_KEY = 'auth_token';
  public readonly REFRESH_TOKEN_KEY = 'refresh_token';
  private isRefreshing = false;

  constructor(private authEventsService: AuthEventsService) { }

  public getToken(): string | null {
    return localStorage.getItem(this.JWT_STORAGE_KEY);
  }

  public setToken(token: string): void {
    localStorage.setItem(this.JWT_STORAGE_KEY, token);
  }

  public clearToken(): void {
    localStorage.removeItem(this.JWT_STORAGE_KEY);
  }

  public hasToken(): boolean {
    return !!this.getToken();
  }

  public isTokenValid(): boolean {
    const token = this.getToken();
    if (!token) return false;

    try {
      const decodedToken = jwtDecode(token) as { exp: number };
      const expirationDate = decodedToken.exp;
      const currentDate = Math.floor(Date.now() / 1000);
      return expirationDate > currentDate;
    } catch (error) {
      console.error('Error decoding token:', error);
      this.clearToken();
      return false;
    }
  }

  public getUserId(): string | null {
    const token = this.getToken();
    if (!token) return null;

    try {
      const decodedToken = jwtDecode(token) as { sub: string };
      return decodedToken.sub;
    } catch {
      return null;
    }
  }

  public getRefreshToken(): string | null {
    return localStorage.getItem(this.REFRESH_TOKEN_KEY);
  }

  public setRefreshToken(token: string): void {
    localStorage.setItem(this.REFRESH_TOKEN_KEY, token);
  }

  public clearRefreshToken(): void {
    localStorage.removeItem(this.REFRESH_TOKEN_KEY);
  }

  public isRefreshTokenValid(): boolean {
    console.log("Getting refresh token...")
    const token = this.getRefreshToken();
    if (!token) {
      console.log("No valid refresh token found")
      return false;
    }

    try {
      const decodedToken = jwtDecode(token) as { exp: number };
      const expirationDate = decodedToken.exp;
      const currentDate = Math.floor(Date.now() / 1000);
      console.log("Refresh token found: ", expirationDate - currentDate)
      return expirationDate > currentDate;
    } catch (error) {
      console.error('Error decoding refresh token:', error);
      this.clearRefreshToken();
      return false;
    }
  }

  /**
   * Ensures that a valid token is available for API calls
   * Attempts to refresh the token if it's expired but refresh token is valid
   * Emits a session expired event if both tokens are invalid
   */
  public async ensureValidToken(): Promise<boolean> {
    console.log("[TokenService] Ensuring valid token...")
    const token = this.getToken();
    
    // Guard: Check if token is valid
    if (token && this.isTokenValid()) {
      console.log("[TokenService] Token is valid")
      return true;
    }
    console.log("[TokenService] Token is invalid or missing");

    // Guard: Check if refresh is already in progress
    if (this.isRefreshing) {
      console.log("[TokenService] Token refresh already in progress, skipping")
      return true;
    }

    // Attempt token refresh
    this.isRefreshing = true;
    try {
      console.log("Refreshing access token...")
      await this.refreshAccessToken();
      return true;
    } catch (error) {
      console.log("Error refreshing access token")
      this.handleSessionExpired();
      return false;
    } finally {
      this.isRefreshing = false;
    }
  }

  /**
   * Refreshes the access token using the refresh token
   */
  private async refreshAccessToken(): Promise<void> {
    const refreshToken = this.getRefreshToken();
    const full_path = `${environment.api_base_url}/auth/refresh`;
    
    const headers = {
      'Content-Type': 'application/json'
    };

    const body = JSON.stringify({ refreshToken });
    const response = await fetch(full_path, { 
      method: 'POST', 
      headers, 
      body 
    });
    
    if (!response.ok) {
      throw new Error(`Refresh token failed: ${response.statusText}`);
    }
    
    const data = await response.json();
    this.setToken(data.accessToken);
    console.log("Access token refreshed")
  }

  /**
   * Handles session expiration by clearing tokens and emitting an event
   */
  private handleSessionExpired(): void {
    console.log("[TokenService] Handling session expiration")
    this.clearToken();
    this.clearRefreshToken();
    
    // Emit session expired event
    console.log("[TokenService] Emitting SESSION_EXPIRED event")
    this.authEventsService.emitEvent({
      type: AuthEventType.SESSION_EXPIRED
    });
  }
}
